// Release wizard-document preview + print (audit M8 + §1 row 17). The server renders the SAME
// template/pipeline the ReleaseDocumentArchiver stores on completion, from the wizard's current step
// values — so the on-screen agreement and the printed PDF are exactly the archived document. The
// access token is bearer-only (in memory, not a cookie), so both calls attach it here; the PDF goes
// through an authenticated blob fetch → object URL, like the invoice exports.
import { getAccessToken } from "../auth";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

export type WizardDocumentTemplateKey =
  | "PLAATSINGSOVEREENKOMST"
  | "RETOUR_EIGENAAR"
  // Intake declarations. RETURN shares AFSTANDSVERKLARING — Jmix prints the same document there.
  | "AFSTANDSVERKLARING"
  | "VINDERSVERKLARING";

/** An HTTP failure carrying the status code so callers can branch. */
export class WizardDocumentError extends Error {
  readonly statusCode: number;
  constructor(statusCode: number, message?: string) {
    super(message ?? `HTTP ${statusCode}`);
    this.statusCode = statusCode;
    this.name = "WizardDocumentError";
  }
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * Fetch the document as PNG page data-URLs for the on-screen agreement (Jmix AdoptionTermsFragment
 * page-image embed). An unrenderable document yields an EMPTY array — the step falls back to its
 * summary text, mirroring the Jmix fragment.
 */
export async function fetchWizardDocumentPreview(
  templateKey: WizardDocumentTemplateKey,
  data: Record<string, unknown>,
): Promise<string[]> {
  const res = await fetch(`${API_URL}/wizard/document/preview`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ templateKey, data }),
  });
  if (!res.ok) throw new WizardDocumentError(res.status, res.statusText);
  const body = (await res.json()) as { pages?: string[] };
  return body.pages ?? [];
}

/** Download the document PDF (the Jmix Print button's `uitboek-afdruk.pdf`). */
export async function downloadWizardDocumentPdf(
  templateKey: WizardDocumentTemplateKey,
  data: Record<string, unknown>,
): Promise<void> {
  const res = await fetch(`${API_URL}/wizard/document/pdf`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ templateKey, data }),
  });
  if (!res.ok) throw new WizardDocumentError(res.status, res.statusText);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "uitboek-afdruk.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
